import React from 'react';
import Gallery from '../pages/hotel/Gallery';
import Foods from '../pages/foods/Foods';
import Room from '../components/Room';
import Articles from '../pages/blog/Articles';
import ScrollToTopButton from './ScrollToTopButton';

function Index({ data, isError, error, isLoading, user }) {

  return (
    <div>
      <Gallery />
      <section className="bg-white py-16">
        <div className="section-shell grid gap-6 md:grid-cols-3">
          <div className="premium-card p-6">
            <div className="mb-5 grid h-12 w-12 place-items-center rounded-2xl bg-gotrip-100 text-gotrip-700">
              <svg
                className="h-6 w-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 10.5 12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1v-10.5Z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-black tracking-tight text-ink">
              Unified reservations
            </h2>
            <p className="mt-3 leading-7 text-muted">
              Manage room stays and restaurant orders from one dashboard.
            </p>
          </div>
          <div className="premium-card p-6">
            <div className="mb-5 grid h-12 w-12 place-items-center rounded-2xl bg-amber-100 text-amber-700">
              <svg
                className="h-6 w-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v6l4 2"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-black tracking-tight text-ink">
              Fast booking flow
            </h2>
            <p className="mt-3 leading-7 text-muted">
              Choose dates, confirm guests, and receive instant booking
              feedback.
            </p>
          </div>
          <div className="premium-card p-6">
            <div className="mb-5 grid h-12 w-12 place-items-center rounded-2xl bg-emerald-100 text-emerald-700">
              <svg
                className="h-6 w-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-black tracking-tight text-ink">
              Trusted details
            </h2>
            <p className="mt-3 leading-7 text-muted">
              Ratings, availability, and transparent pricing before you commit.
            </p>
          </div>
        </div>
      </section>
      <Room
        data={data}
        isError={isError}
        error={error}
        isLoading={isLoading}
        user={user}
        sliceFirstThree
      >
        <div>
          <span className="eyebrow">Featured stays</span>
          <h2 className="section-title mt-5">
            Rooms designed for booking clarity
          </h2>
          <p className="section-copy mx-auto">
            A responsive room listing experience that emphasizes availability,
            details, ratings, and the next booking action.
          </p>
        </div>
      </Room>
      <Foods user={user} />
      <section className="bg-slate-950 py-20 text-white">
        <div className="section-shell grid items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <span className="inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.22em] text-white">
              Full-Stack MVP
            </span>
            <h2 className="mt-5 text-3xl font-black tracking-tight sm:text-5xl">
              Built like a product, not a template.
            </h2>
            <p className="mt-5 max-w-xl text-lg leading-8 text-slate-300">
              GoTrip combines hospitality UX with modern management workflows,
              creating a portfolio-ready reservation platform.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl border border-white/10 bg-white/10 p-6 backdrop-blur">
              <p className="text-3xl font-black">2-in-1</p>
              <p className="mt-2 text-sm font-semibold text-slate-300">
                Hotels and restaurants in one flow
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/10 p-6 backdrop-blur">
              <p className="text-3xl font-black">Mobile-first</p>
              <p className="mt-2 text-sm font-semibold text-slate-300">
                Responsive cards, forms, and checkout
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/10 p-6 backdrop-blur sm:col-span-2">
              <p className="text-3xl font-black">Premium UX</p>
              <p className="mt-2 text-sm font-semibold text-slate-300">
                Clear hierarchy, soft surfaces, and conversion-focused actions
              </p>
            </div>
          </div>
        </div>
      </section>
      <Articles />
      <section className="bg-surface py-16">
        <div className="section-shell">
          <div className="mx-auto max-w-3xl text-center">
            <span className="eyebrow">Built by Rashin Web Dev</span>
            <h1 className="section-title mt-5">GoTrip reservation platform</h1>
            <p className="section-copy mx-auto">
              A full-stack hotel and restaurant booking MVP built with product
              thinking, modern UI/UX, and scalable architecture.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <article className="premium-card p-6">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-gotrip-700">
                Problem
              </p>
              <h2 className="mt-3 text-xl font-black text-ink">
                Fragmented booking workflows
              </h2>
              <p className="mt-3 leading-7 text-muted">
                Rooms, dining, accounts, and reservations can feel disconnected
                for guests and operators.
              </p>
            </article>
            <article className="premium-card p-6">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-gotrip-700">
                Transformation
              </p>
              <h2 className="mt-3 text-xl font-black text-ink">
                Unified product experience
              </h2>
              <p className="mt-3 leading-7 text-muted">
                The platform brings room discovery, restaurant ordering,
                favorites, cart, and dashboard flows into one interface.
              </p>
            </article>
            <article className="premium-card p-6">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-gotrip-700">
                Result
              </p>
              <h2 className="mt-3 text-xl font-black text-ink">
                Clearer booking journey
              </h2>
              <p className="mt-3 leading-7 text-muted">
                Users get structured details, responsive layouts, and direct
                paths from discovery to reservation actions.
              </p>
            </article>
          </div>
        </div>
      </section>
      <ScrollToTopButton />
    </div>
  );
}

export default Index;
